// JOBE-0044.6: built-in saved views (R5 §1).
//
// Six entries, each with id, star, glyph, label, and a queryTemplate
// function `(ctx) => FilterChip[]`. The template resolves the current
// principal and time-relative anchors (`since=<today 00:00>`,
// `since=<monday 00:00>`, etc.) at call time rather than ship a stale
// URL fragment.
//
// Stars are non-editable in v1 — the design pins which two are starred,
// and the user can't rearrange. JOBE-0044.9 owns the editing UI when
// user-saved views land.
//
// `untagged-30d` is a placeholder per the spec: the "no collection
// chip set + the entity has no `memberOf` edges" semantic is not
// expressible in the chip vocabulary today (JOBE-0044.10 ships the
// negation chips). The view still appears in the rail — clicking it
// surfaces the `since` chip only, and the tooltip explains the gap.

import { FRIENDLY_TOKENS, makeScopeChip } from './chipVocabulary.js';

// ─── Time anchors ────────────────────────────────────────────────────

function startOfToday(now) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function startOfWeek(now) {
  // Monday 00:00 local. Mirrors the design ("Mine, this week" → Monday
  // anchor rather than rolling 7-day window).
  const d = new Date(now);
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ...
  const offsetToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offsetToMonday);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function daysAgo(now, days) {
  const d = new Date(now - days * 86_400_000);
  return d.toISOString();
}

// ─── Chip builders ──────────────────────────────────────────────────

function predicateChip(friendlyToken, values) {
  const entry = FRIENDLY_TOKENS[friendlyToken];
  return {
    kind: 'predicate',
    friendlyToken,
    wirePredicate: entry?.wirePredicate,
    values,
  };
}

// ─── Built-in registry ──────────────────────────────────────────────

export const BUILT_IN_SAVED_VIEWS = [
  {
    id: 'todays-briefing',
    starred: true,
    glyph: '★',
    label: "Today's briefing",
    description: 'Briefings committed since midnight.',
    queryTemplate: (ctx) => [
      makeScopeChip(),
      predicateChip('type', ['briefing']),
      predicateChip('since', [startOfToday(ctx.now)]),
    ],
  },
  {
    id: 'mine-this-week',
    starred: true,
    glyph: '★',
    label: 'Mine, this week',
    description: 'Documents you committed since Monday.',
    queryTemplate: (ctx) => {
      const chips = [makeScopeChip()];
      if (ctx.principalUrn) {
        chips.push(predicateChip('by', [ctx.principalUrn]));
      }
      chips.push(predicateChip('since', [startOfWeek(ctx.now)]));
      return chips;
    },
  },
  {
    id: 'dept-head-reports',
    starred: false,
    glyph: '·',
    label: 'Dept-head reports',
    description: 'Reports authored by department heads.',
    queryTemplate: () => [
      makeScopeChip(),
      predicateChip('type', ['report']),
      // `roleInDepartment=head` is a cross-facet predicate that resolves
      // to a multi-step query in JOBE-0044.10; v1 renders the chip set
      // verbatim and accepts the wire returns documents-only.
    ],
  },
  {
    id: 'research-drops',
    starred: false,
    glyph: '·',
    label: 'Research drops',
    description: 'Research notes from the last 14 days.',
    queryTemplate: (ctx) => [
      makeScopeChip(),
      predicateChip('type', ['research-note']),
      predicateChip('since', [daysAgo(ctx.now, 14)]),
    ],
  },
  {
    id: 'untagged-30d',
    starred: false,
    glyph: '·',
    label: 'Untagged in last 30d',
    description: 'Pending JOBE-0044.10\'s negation chips — renders the time window only.',
    queryTemplate: (ctx) => [
      makeScopeChip(),
      // `inCollection=∅` semantic is not expressible in v1; ship the
      // time window so the view does *something* and the tooltip
      // explains the gap.
      predicateChip('since', [daysAgo(ctx.now, 30)]),
    ],
  },
  {
    id: 'receipts',
    starred: false,
    glyph: '·',
    label: 'Receipts',
    description: 'Everything of kind receipt.',
    queryTemplate: () => [
      makeScopeChip(),
      predicateChip('type', ['receipt']),
    ],
  },
];

// resolveViewToChips: given a built-in view id and a runtime context
// (`{ now, principalUrn }`), return the resolved FilterChip[].
// Returns `null` when the id is unknown. Always includes the scope
// chip at index 0 (the template builders prepend it for us).
export function resolveViewToChips(viewId, ctx = {}) {
  const view = BUILT_IN_SAVED_VIEWS.find((v) => v.id === viewId);
  if (!view) return null;
  const resolved = view.queryTemplate({
    now: ctx.now ?? Date.now(),
    principalUrn: ctx.principalUrn ?? null,
  });
  return resolved;
}
